import { useTranslation } from "react-i18next";
import type { Item, MenuResponse } from "../../../api/menu";
import { getExceededLunchLimits } from "../../../api/lunchMenus";
import { lunchRemainingOf, lunchRoomLeft } from "../../../lib/lunchCatalog";
import { getItemName } from "../../../utils/translations";
import type { useOrderingMessage } from "../components/useOrderingMessage";

interface CartLine {
  itemId: number;
  quantity: number;
}

interface LunchLimitsOptions {
  allItems: Item[];
  orderItems: CartLine[];
  message: ReturnType<typeof useOrderingMessage>;
}

/**
 * Дневният лимит на обедните порции в „Маркиране" (BE
 * `bills/service_lunch_limits.py`). Каталогът знае колко остават към
 * последното меню (`Item.lunchRemaining`); тук кошницата не може да вземе
 * повече, а сервитьорът разбира защо. Истината е сървърът: двама, взели
 * последната порция едновременно — вторият получава 409 `lunch_limit_reached`.
 */
export const useLunchLimits = ({ allItems, orderItems, message }: LunchLimitsOptions) => {
  const { t, i18n } = useTranslation();
  const nameOf = (item: Item) => getItemName(item.itemLangs, i18n.language, item.slug);

  const warn = (item: Item, left: number) =>
    message.warning(
      left <= 0
        ? t("lunchMenu.limitSoldOut", { name: nameOf(item) })
        : t("lunchMenu.limitOnlyLeft", { name: nameOf(item), count: left }),
    );

  const cartQuantityOf = (itemId: number) =>
    orderItems
      .filter((line) => line.itemId === itemId)
      .reduce((sum, line) => sum + line.quantity, 0);

  /**
   * Колко от `wanted` порции могат да се добавят (при `inCart` вече в
   * кошницата). Не стигат ли — казва колко остават; 0 → изчерпано.
   */
  const allowedQuantity = (itemId: number, wanted: number, inCart = cartQuantityOf(itemId)) => {
    const item = allItems.find((candidate) => candidate.id === itemId);
    if (!item) return wanted;
    const room = lunchRoomLeft(item, inCart);
    if (wanted <= room) return wanted;
    warn(item, room);
    return room;
  };

  /** „Поръчай пак" на обеден ред: изчерпан ли е по свежото меню (и казва го). */
  const isSoldOutFresh = (menu: MenuResponse | undefined, lunchMenuId: number, productId: number) => {
    const lunchItem = menu?.lunchMenus
      ?.find((lunchMenu) => lunchMenu.id === lunchMenuId)
      ?.items.find((candidate) => candidate.itemId === productId);
    const left = lunchItem ? lunchRemainingOf(lunchItem) : null;
    if (left == null || left > 0) return false;
    const item = allItems.find((candidate) => candidate.id === productId);
    if (item) warn(item, 0);
    return true;
  };

  /** Текстът за 409 `lunch_limit_reached`, иначе null. */
  const limitErrorMessage = (error: unknown) => {
    const exceeded = getExceededLunchLimits(error);
    if (exceeded == null) return null;
    const items = exceeded
      .map((entry) => {
        const item = allItems.find((candidate) => candidate.id === entry.itemId);
        return t("lunchMenu.limitReachedItem", {
          name: item ? nameOf(item) : `#${entry.itemId}`,
          count: entry.remaining,
        });
      })
      .join(", ");
    return t("lunchMenu.limitReachedOrder", { items });
  };

  return { allowedQuantity, isSoldOutFresh, limitErrorMessage };
};
